import { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  BookOpen,
  FolderOpen,
  PlayCircle,
  ChevronRight,
  ChevronDown,
  Plus,
  X,
  GripVertical,
  CheckCircle2,
  Clock,
  FileText,
  Eye,
  ImagePlus,
  FileUp,
  Link2,
  Search,
  Trash2,
} from 'lucide-react'
import {
  printBookApi,
  type PrintBookModuleRequest,
  type PrintBookCreateRequest,
  type PrintBookUpdateRequest,
  type PrintBookMaterialType,
  type PrintBookScopeLevel,
  type PrintBookSourceType,
  type PrintBookStatus,
  PRINT_BOOK_STATUSES,
  PRINT_BOOK_STATUS_LABELS,
  toPrintBookStatus,
} from '@/api/printBook'
import { adminApi, type AdminContent } from '@/api/admin'
import { apiClient } from '@/api/client'
import { ContentLevelBadge, ContentPickerModal } from '@/components/content/ContentPickerModal'
import type { ApiResponse } from '@/types/api'
import type { BookListRouteState } from './bookNavigation'
import { parsePrintBookPageCsv } from './printBookPageCsv'
import { BookImageUploadField } from './BookImageUploadField'
import { PrintBookScopeHelp } from './PrintBookScopeHelp'

// ─── Utilities ──────────────────────────────────────────────────────

function parseGradeField(raw: string): { grade: string; unitLabel: string; unitName: string } | null {
  // Strip prefix like (깨튜브) if present
  const cleaned = raw.replace(/^\([^)]*\)\s*/, '')
  // Match pattern: [초등 4-2-3] 소수의 덧셈과 뺄셈
  const match = cleaned.match(/\[(\S+)\s+(\d+)-(\d+)-(\d+)\]\s*(.+)/)
  if (!match) return null
  return {
    grade: `${match[1]} ${match[2]}-${match[3]}`,
    unitLabel: `${match[4]}단원`,
    unitName: match[5].trim(),
  }
}

// ─── Types ──────────────────────────────────────────────────────────

interface BookForm {
  title: string
  subtitle: string
  bookLabel: string
  description: string
  coverImageUrl: string | null
  learnerCoverImageUrl: string | null
  materialType: PrintBookMaterialType | ''
  volumeLabel: string
  scopeLevel: PrintBookScopeLevel | ''
  scopeContentId: number | null
  sourceType: PrintBookSourceType
  status: PrintBookStatus
  noticeEnabled: boolean
  noticeMessage: string
  samplePdfUrl: string
  nextBookId: number | null
  happydayItemCode: number | null
  happydayIsbnCode: string
}

interface SelectedModule {
  moduleId: number
  moduleName: string
  bareTitle: string | null
  sortOrder: number
  chapterNumber: number
  titleOverride: string | null
  moduleSummaryStart?: number
  lessons: SelectedLesson[]
}

interface SelectedLesson {
  lessonId: number
  lessonName: string
  bareTitle: string | null
  grade: string | null
  sortOrder: number
  pageCount: number | null
  startPage: number | null
  blockQuizStartPage: number | null
  hasMemoPage: boolean
  confirmed: boolean
}

const DEFAULT_FORM: BookForm = {
  title: '',
  subtitle: '',
  bookLabel: '',
  description: '',
  coverImageUrl: null,
  learnerCoverImageUrl: null,
  materialType: '',
  volumeLabel: '',
  scopeLevel: '',
  scopeContentId: null,
  sourceType: 'GENERATED_PREVIEW',
  status: 'draft',
  noticeEnabled: false,
  noticeMessage: '',
  samplePdfUrl: '',
  nextBookId: null,
  happydayItemCode: null,
  happydayIsbnCode: '',
}

const TOC_PAGE_COUNT = 2

// ─── Page Calculation ───────────────────────────────────────────────

// 페이지 구조:
// p.1: 속표지 (1p)
// p.2~: 목차 (tocPageCount)
// 모듈마다: 모듈 요약 (2p) + 레슨들
// 레슨은 항상 짝수 페이지(왼쪽)에서 시작, 필요 시 메모 페이지 삽입
const COVER_PAGE_COUNT = 1
const MODULE_SUMMARY_PAGE_COUNT = 2

function getModuleDisplayName(module: SelectedModule): string {
  return module.titleOverride?.trim() || module.bareTitle || module.moduleName
}

function calculatePages(tocPageCount: number, modules: SelectedModule[]): SelectedModule[] {
  let currentPage = COVER_PAGE_COUNT + tocPageCount + 1 // 속표지 + 목차 이후

  return modules.map(mod => {
    // 모듈 요약 2페이지
    // 모듈 요약은 짝수에서 시작해야 첫 레슨이 짝수에서 시작
    if (currentPage % 2 !== 0) currentPage++ // 홀수면 패딩
    const moduleSummaryStart = currentPage
    currentPage += MODULE_SUMMARY_PAGE_COUNT // 2페이지

    return {
      ...mod,
      moduleSummaryStart,
      lessons: mod.lessons.map(lesson => {
        // 레슨은 항상 짝수 페이지에서 시작
        if (currentPage % 2 !== 0) currentPage++ // 홀수면 더미 삽입

        const startPage = currentPage
        const pageCount = lesson.pageCount || 0
        const endPage = startPage + pageCount - 1
        currentPage = endPage + 1

        // 더미 필요 여부: 레슨이 홀수에서 끝나면 다음이 짝수 → 더미 불필요
        // 레슨이 짝수에서 끝나면 다음이 홀수 → 더미 필요
        const hasMemoPage = pageCount > 0 && (endPage % 2 === 0)
        if (hasMemoPage) currentPage++ // 메모 페이지 포함

        return {
          ...lesson,
          startPage,
          hasMemoPage,
        }
      })
    }
  })
}

function getTotalPageCount(tocPageCount: number, modules: SelectedModule[]): number {
  const calculated = calculatePages(tocPageCount, modules)
  let maxPage = COVER_PAGE_COUNT + tocPageCount
  calculated.forEach(mod => {
    // 모듈 요약 페이지
    if (mod.moduleSummaryStart) {
      const modEnd = mod.moduleSummaryStart + MODULE_SUMMARY_PAGE_COUNT - 1
      if (modEnd > maxPage) maxPage = modEnd
    }
    mod.lessons.forEach(lesson => {
      if (lesson.startPage && lesson.pageCount) {
        const end = lesson.startPage + lesson.pageCount - 1 + (lesson.hasMemoPage ? 1 : 0)
        if (end > maxPage) maxPage = end
      }
    })
  })
  return maxPage
}

// ─── Content Tree ───────────────────────────────────────────────────

function ContentTree({ onSelectModule }: {
  onSelectModule: (moduleId: number, moduleName: string, chapterNumber: number, lessons: { id: number; name: string }[]) => void
}) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const { data: contents, isLoading } = useQuery({
    queryKey: ['admin-contents-tree', 'curriculum'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
  })

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) return <div className="p-4 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />로딩 중...</div>

  return (
    <div className="text-sm">
      {contents?.map((curr: AdminContent) => (
        <ContentNode
          key={curr.id}
          content={curr}
          level={0}
          positionInParent={0}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          onSelectModule={onSelectModule}
        />
      ))}
    </div>
  )
}

function ContentNode({ content, level, positionInParent, expandedIds, onToggle, onSelectModule }: {
  content: AdminContent
  level: number
  positionInParent: number
  expandedIds: Set<number>
  onToggle: (id: number) => void
  onSelectModule: (moduleId: number, moduleName: string, chapterNumber: number, lessons: { id: number; name: string }[]) => void
}) {
  const isExpanded = expandedIds.has(content.id)
  const isModule = content.hierarchyLevel === 'module'
  const isLesson = content.hierarchyLevel === 'lesson'
  const canExpand = !isLesson

  const { data: children } = useQuery({
    queryKey: ['admin-content-children', content.id],
    queryFn: () => adminApi.getChildContents(content.id),
    enabled: isExpanded && canExpand,
  })

  const Icon = isLesson ? PlayCircle : isModule ? FolderOpen : BookOpen

  const handleAddModule = async () => {
    if (!isModule) return
    // Fetch children if not loaded yet
    const lessonData = children || await adminApi.getChildContents(content.id)
    const lessons = lessonData.map((c: AdminContent) => ({
      id: c.id,
      name: c.title,
    }))
    onSelectModule(content.id, content.title, positionInParent, lessons)
  }

  return (
    <div>
      <div
        className="flex items-center gap-1.5 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer"
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => canExpand ? onToggle(content.id) : undefined}
      >
        {canExpand ? (
          isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="truncate text-slate-700"><ContentMetadataText text={content.title} /></span>
        {isModule && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleAddModule()
            }}
            className="ml-auto p-0.5 text-amber-500 hover:bg-amber-50 rounded shrink-0"
            title="모듈 추가"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {isExpanded && children?.map((child: AdminContent, childIndex: number) => (
        <ContentNode
          key={child.id}
          content={child}
          level={level + 1}
          positionInParent={childIndex + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onSelectModule={onSelectModule}
        />
      ))}
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────

export function BookEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isEdit = !!id
  const routeState = location.state as BookListRouteState | null

  const [form, setForm] = useState<BookForm>({ ...DEFAULT_FORM })
  const [modules, setModules] = useState<SelectedModule[]>([])
  // 수동 페이지 입력(startPage/pageCount)에 미저장 변경이 있는지 여부.
  // ref 는 hydration effect 가 form/modules 재설정 없이 최신 dirty 상태를 읽기 위한 미러.
  const [pagesDirty, setPagesDirty] = useState(false)
  const pagesDirtyRef = useRef(false)
  const markPagesDirty = () => {
    pagesDirtyRef.current = true
    setPagesDirty(true)
  }
  const clearPagesDirty = () => {
    pagesDirtyRef.current = false
    setPagesDirty(false)
  }
  // 블록퀴즈 시작 페이지는 기존 페이지 구성 저장과 독립적으로 관리한다.
  const [blockQuizPagesDirty, setBlockQuizPagesDirty] = useState(false)
  const blockQuizPagesDirtyRef = useRef(false)
  const markBlockQuizPagesDirty = () => {
    blockQuizPagesDirtyRef.current = true
    setBlockQuizPagesDirty(true)
  }
  const clearBlockQuizPagesDirty = () => {
    blockQuizPagesDirtyRef.current = false
    setBlockQuizPagesDirty(false)
  }
  const [searchParams, setSearchParams] = useSearchParams()
  const [step, setStepState] = useState(() => {
    const tabParam = searchParams.get('tab')
    return tabParam ? Number(tabParam) : 1
  })
  const setStep = (n: number) => {
    setStepState(n)
    setSearchParams({ tab: String(n) }, { replace: true })
  }

  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pageCsvFileInputRef = useRef<HTMLInputElement>(null)
  const [isScopePickerOpen, setIsScopePickerOpen] = useState(false)
  const [isPageCsvOpen, setIsPageCsvOpen] = useState(false)
  const [pageCsvText, setPageCsvText] = useState('')

  // Load existing book
  const { data: book, isLoading: bookLoading } = useQuery({
    queryKey: ['print-book', id],
    queryFn: () => printBookApi.getById(Number(id)),
    enabled: isEdit,
    refetchOnMount: 'always',
  })

  // 다음 권 선택용 전체 교재 목록
  const { data: bookList } = useQuery({
    queryKey: ['print-books'],
    queryFn: printBookApi.getAll,
  })

  const { data: scopeContent, isLoading: scopeContentLoading } = useQuery({
    queryKey: ['admin-content', form.scopeContentId],
    queryFn: () => adminApi.getContentById(form.scopeContentId!),
    enabled: form.scopeContentId != null && (form.scopeLevel === 'CURRICULUM' || form.scopeLevel === 'COURSE'),
  })

  // book 데이터 변경 시 로컬 state 동기화 (updatedAt 포함하여 변경 감지)
  const bookUpdatedAt = book?.updatedAt
  useEffect(() => {
    if (book) {
      setForm({
        title: book.title,
        subtitle: book.subtitle || '',
        bookLabel: book.bookLabel,
        description: book.description || '',
        coverImageUrl: book.coverImageUrl ?? null,
        learnerCoverImageUrl: book.learnerCoverImageUrl ?? null,
        materialType: book.materialType ?? '',
        volumeLabel: book.volumeLabel ?? '',
        scopeLevel: book.scopeLevel ?? '',
        scopeContentId: book.scopeContentId ?? null,
        sourceType: book.sourceType ?? 'GENERATED_PREVIEW',
        status: toPrintBookStatus(book.status),
        noticeEnabled: book.noticeEnabled ?? false,
        noticeMessage: book.noticeMessage ?? '',
        samplePdfUrl: book.samplePdfUrl ?? '',
        nextBookId: book.nextBookId ?? null,
        happydayItemCode: book.happydayItemCode ?? null,
        happydayIsbnCode: book.happydayIsbnCode ?? '',
      })
      setModules(prev => {
        // 미저장 수동 페이지 입력이 있으면 (예: 컨펌 토글 refetch) 서버값으로 덮지 않고
        // 로컬 startPage/pageCount 를 보존한다. 나머지 필드(confirmed 등)는 서버값 적용.
        const localPages = pagesDirtyRef.current
          ? new Map(prev.flatMap(m => m.lessons).map(l => [l.lessonId, { startPage: l.startPage, pageCount: l.pageCount }]))
          : null
        const localBlockQuizPages = blockQuizPagesDirtyRef.current
          ? new Map(prev.flatMap(m => m.lessons).map(l => [l.lessonId, l.blockQuizStartPage]))
          : null
        return book.modules.map(m => ({
          moduleId: m.moduleId,
          moduleName: m.moduleName || `Module ${m.moduleId}`,
          bareTitle: m.bareTitle,
          sortOrder: m.sortOrder,
          chapterNumber: m.chapterNumber,
          titleOverride: m.titleOverride,
          lessons: m.lessons.map(l => {
            const local = localPages?.get(l.lessonId)
            return {
              lessonId: l.lessonId,
              lessonName: l.lessonName || `Lesson ${l.lessonId}`,
              bareTitle: l.bareTitle,
              grade: l.grade,
              sortOrder: l.sortOrder,
              pageCount: local ? local.pageCount : l.pageCount,
              startPage: local ? local.startPage : l.startPage,
              blockQuizStartPage: localBlockQuizPages?.has(l.lessonId)
                ? localBlockQuizPages.get(l.lessonId) ?? null
                : l.blockQuizStartPage,
              hasMemoPage: l.hasMemoPage,
              confirmed: l.confirmed,
            }
          })
        }))
      })
    }
  }, [book, bookUpdatedAt])

  const calculatedModules = useMemo(
    () => calculatePages(TOC_PAGE_COUNT, modules),
    [TOC_PAGE_COUNT, modules]
  )
  const totalPages = useMemo(
    () => getTotalPageCount(TOC_PAGE_COUNT, modules),
    [TOC_PAGE_COUNT, modules]
  )

  // 외부 제작본은 프리뷰 레이아웃 파생 계산(속표지/목차/모듈요약/짝수정렬/메모)이 무의미 —
  // 수동 입력값(raw modules)을 그대로 사용한다.
  const isExternalDesign = form.sourceType === 'EXTERNAL_DESIGN'
  // 서버에 저장된 제작 방식. 수동 페이지 저장은 서버 상태 기준으로만 허용 —
  // 로컬에서만 외부 제작본으로 바꾼 상태에서 저장하면, 서버는 아직 GENERATED_PREVIEW 라
  // 전체 프리뷰 autosave 가드를 통과해 수동 페이지가 덮어써질 수 있다.
  const serverExternalDesign = book?.sourceType === 'EXTERNAL_DESIGN'
  const displayModules = isExternalDesign ? modules : calculatedModules
  const manualTotalPages = useMemo(() => {
    let maxPage = 0
    modules.forEach(m => m.lessons.forEach(l => {
      // 유효 규칙: startPage >= 1 && pageCount >= 1 인 레슨만 집계
      if (l.startPage != null && l.pageCount != null && l.startPage >= 1 && l.pageCount >= 1) {
        const end = l.startPage + l.pageCount - 1
        if (end > maxPage) maxPage = end
      }
    }))
    return maxPage
  }, [modules])
  const displayTotalPages = isExternalDesign ? manualTotalPages : totalPages
  const validLessonIds = useMemo(
    () => new Set(modules.flatMap(module => module.lessons.map(lesson => lesson.lessonId))),
    [modules],
  )
  const pageCsvResult = useMemo(
    () => parsePrintBookPageCsv(pageCsvText, validLessonIds),
    [pageCsvText, validLessonIds],
  )
  const currentBookId = Number(id)
  const incomingBooks = useMemo(
    () => (bookList ?? []).filter(listBook => listBook.nextBookId === currentBookId),
    [bookList, currentBookId],
  )
  const nextBook = useMemo(
    () => (bookList ?? []).find(listBook => listBook.id === form.nextBookId) ?? null,
    [bookList, form.nextBookId],
  )

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async () => {
      const moduleRequests: PrintBookModuleRequest[] = modules.map((m, mi) => ({
        moduleId: m.moduleId,
        sortOrder: mi,
        chapterNumber: m.chapterNumber,
        titleOverride: m.titleOverride?.trim() || null,
        lessons: m.lessons.map((l, li) => ({
          lessonId: l.lessonId,
          sortOrder: li,
        }))
      }))

      // 빈 문자열은 백엔드로 보내지 않고 null 로 변환한다.
      const metadataFields = {
        materialType: form.materialType || null,
        volumeLabel: form.volumeLabel.trim() || null,
        scopeLevel: form.scopeLevel || null,
        // GLOBAL/미설정이면 scopeContentId 는 의미 없음 — null 로 정리
        scopeContentId:
          form.scopeLevel === 'CURRICULUM' || form.scopeLevel === 'COURSE'
            ? form.scopeContentId
            : null,
        sourceType: form.sourceType,
        noticeEnabled: form.noticeEnabled,
        noticeMessage: form.noticeMessage.trim() || null,
        samplePdfUrl: form.samplePdfUrl.trim() || null,
        nextBookId: form.nextBookId,
        happydayItemCode: form.happydayItemCode,
        happydayIsbnCode: form.happydayIsbnCode.trim() || null,
      }

      if (isEdit) {
        const req: PrintBookUpdateRequest = {
          title: form.title,
          subtitle: form.subtitle || undefined,
          bookLabel: form.bookLabel,
          description: form.description || undefined,
          coverImageUrl: form.coverImageUrl,
          learnerCoverImageUrl: form.learnerCoverImageUrl,
          modules: moduleRequests,
          status: form.status,
          ...metadataFields,
        }
        return printBookApi.update(Number(id), req)
      } else {
        const req: PrintBookCreateRequest = {
          title: form.title,
          subtitle: form.subtitle || undefined,
          bookLabel: form.bookLabel,
          description: form.description || undefined,
          coverImageUrl: form.coverImageUrl,
          learnerCoverImageUrl: form.learnerCoverImageUrl,
          modules: moduleRequests,
          ...metadataFields,
        }
        return printBookApi.create(req)
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['print-books'] })
      queryClient.invalidateQueries({ queryKey: ['print-book', id] })
      if (!isEdit) {
        navigate(`/contents/books/${data.id}`, { replace: true, state: routeState })
      }
    },
  })

  const confirmMutation = useMutation({
    mutationFn: ({ lessonId, confirmed }: { lessonId: number; confirmed: boolean }) =>
      printBookApi.confirmLesson(Number(id), lessonId, confirmed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-book', id] })
    },
  })

  // 외부 제작본 전용: 수동 입력한 페이지 범위를 raw modules 값 그대로 저장
  // (calculatedModules 를 쓰면 파생 startPage 가 저장되어 수동 입력이 무의미해짐)
  const manualPagesMutation = useMutation({
    mutationFn: () =>
      printBookApi.updatePages(Number(id), {
        // 외부 제작본은 파생 total 이 무의미 — 프리뷰 autosave 와 동일하게 0 전송
        totalPageCount: 0,
        lessons: modules.flatMap(m => m.lessons.map(l => ({
          lessonId: l.lessonId,
          pageCount: l.pageCount ?? 0,
          startPage: l.startPage ?? 0,
          hasMemoPage: false,
          blockQuizStartPage: l.blockQuizStartPage,
          updateBlockQuizStartPage: true,
        }))),
      }),
    onSuccess: () => {
      clearPagesDirty()
      clearBlockQuizPagesDirty()
      queryClient.invalidateQueries({ queryKey: ['print-book', id] })
    },
  })

  const blockQuizPagesMutation = useMutation({
    mutationFn: () =>
      printBookApi.updateBlockQuizPages(Number(id), {
        lessons: modules.flatMap(m => m.lessons.map(l => ({
          lessonId: l.lessonId,
          blockQuizStartPage: l.blockQuizStartPage,
        }))),
      }),
    onSuccess: () => {
      clearBlockQuizPagesDirty()
      queryClient.invalidateQueries({ queryKey: ['print-book', id] })
    },
  })

  const handleSaveManualPages = () => {
    if (!id || id === 'new' || book?.sourceType !== 'EXTERNAL_DESIGN') return
    manualPagesMutation.mutate()
  }

  const handleBackToList = () => {
    navigate(routeState?.returnTo ?? '/contents/books', {
      replace: true,
      state: {
        restoreScrollY: routeState?.listScrollY,
        activeBookId: routeState?.activeBookId ?? (Number.isInteger(currentBookId) ? currentBookId : undefined),
      } satisfies BookListRouteState,
    })
  }

  const navigateToConnectedBook = (bookId: number) => {
    navigate(`/contents/books/${bookId}`, {
      state: routeState ? { ...routeState, activeBookId: bookId } satisfies BookListRouteState : undefined,
    })
  }

  const pageCsvTemplate = useMemo(() => {
    const rows = modules.flatMap(module => module.lessons).map(lesson => (
      `${lesson.lessonId},${lesson.startPage ?? ''},${lesson.pageCount ?? ''},${lesson.blockQuizStartPage ?? ''}`
    ))
    return ['lessonId,startPage,pageCount,blockQuizStartPage', ...rows].join('\n')
  }, [modules])

  const openPageCsv = () => {
    setPageCsvText('lessonId,startPage,pageCount,blockQuizStartPage\n')
    setIsPageCsvOpen(true)
  }

  const handlePageCsvFile = async (file: File | undefined) => {
    if (!file) return
    setPageCsvText(await file.text())
    if (pageCsvFileInputRef.current) pageCsvFileInputRef.current.value = ''
  }

  const applyPageCsv = () => {
    if (pageCsvResult.hasErrors || pageCsvResult.rows.length === 0) return
    const pageValuesByLessonId = new Map(pageCsvResult.rows.map(row => [row.lessonId, row]))
    setModules(currentModules => currentModules.map(module => ({
      ...module,
      lessons: module.lessons.map(lesson => {
        const pageValues = pageValuesByLessonId.get(lesson.lessonId)
        return pageValues
          ? {
              ...lesson,
              startPage: pageValues.startPage,
              pageCount: pageValues.pageCount,
              hasMemoPage: false,
              ...('blockQuizStartPage' in pageValues
                ? { blockQuizStartPage: pageValues.blockQuizStartPage ?? null }
                : {}),
            }
          : lesson
      }),
    })))
    markPagesDirty()
    if (pageCsvResult.rows.some(row => 'blockQuizStartPage' in row)) {
      markBlockQuizPagesDirty()
    }
    setIsPageCsvOpen(false)
  }

  const handleAddModule = useCallback((moduleId: number, moduleName: string, chapterNumber: number, lessons: { id: number; name: string }[]) => {
    setModules(prev => {
      if (prev.some(m => m.moduleId === moduleId)) return prev
      return [...prev, {
        moduleId,
        moduleName,
        bareTitle: null,
        sortOrder: prev.length,
        chapterNumber,
        titleOverride: null,
        lessons: lessons.map((l, i) => ({
          lessonId: l.id,
          lessonName: l.name,
          bareTitle: null,
          grade: null,
          sortOrder: i,
          pageCount: null,
          startPage: null,
          blockQuizStartPage: null,
          hasMemoPage: false,
          confirmed: false,
        }))
      }]
    })
  }, [])

  const handleRemoveModule = (moduleId: number) => {
    setModules(prev => prev.filter(m => m.moduleId !== moduleId))
  }

  const handleCoverFileSelected = async (file: File | undefined) => {
    if (!file) return
    setUploadError(null)

    // Reset the file input value so re-selecting the same file fires onChange again
    if (fileInputRef.current) fileInputRef.current.value = ''

    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp']
    if (!ALLOWED.includes(file.type)) {
      setUploadError('JPG, PNG, WebP만 업로드 가능합니다.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('파일 크기는 5MB 이하여야 합니다.')
      return
    }

    setIsUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('type', 'book-cover')
      const { data } = await apiClient.post<ApiResponse<{ url: string; filename: string }>>(
        '/admin/upload/image',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      const url = data?.data?.url
      if (url) {
        setForm(prev => ({ ...prev, coverImageUrl: url }))
      } else {
        setUploadError('업로드 응답이 비어있습니다.')
      }
    } catch (err) {
      console.error('[BookEditPage] cover image upload failed', err)
      setUploadError('업로드에 실패했습니다.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleCoverDelete = () => {
    setForm(prev => ({ ...prev, coverImageUrl: null }))
    setUploadError(null)
  }

  const handleFormChange = <K extends keyof BookForm>(key: K, value: BookForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  if (bookLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={handleBackToList} className="p-2 hover:bg-slate-100 rounded-lg" aria-label="교재 목록으로">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEdit ? form.title || '교재 편집' : '새 교재 만들기'}
            </h1>
            <p className="text-sm text-slate-500">
              {isEdit ? `총 ${displayTotalPages}페이지` : '기본 정보를 입력하고 모듈을 선택하세요'}
            </p>
          </div>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !form.title}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
        >
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          저장
        </button>
      </div>

      {saveMutation.isError && (
        <div className="flex items-center gap-2 p-3 mb-4 text-red-700 bg-red-50 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" />
          저장에 실패했습니다.
        </div>
      )}

      {/* Steps */}
      <div className="flex gap-1 mb-6">
        {[
          { n: 1, label: '기본 정보' },
          { n: 2, label: '모듈 & 레슨' },
          { n: 3, label: '페이지 요약' },
        ].map(s => (
          <button
            key={s.n}
            onClick={() => setStep(s.n)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              step === s.n
                ? 'bg-amber-500 text-slate-900'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            {s.n}. {s.label}
          </button>
        ))}
      </div>

      {isEdit && (incomingBooks.length > 0 || nextBook) && (
        <section className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Link2 className="h-4 w-4 text-amber-600" /> 연결된 교재
          </span>
          {incomingBooks.map(incomingBook => (
            <button
              key={incomingBook.id}
              type="button"
              onClick={() => navigateToConnectedBook(incomingBook.id)}
              className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-left text-xs text-slate-700 transition hover:border-amber-400 hover:text-amber-800"
            >
              <span className="mr-1 text-slate-400">이전</span>
              {incomingBook.volumeLabel || incomingBook.title}
            </button>
          ))}
          {nextBook && (
            <button
              type="button"
              onClick={() => navigateToConnectedBook(nextBook.id)}
              className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-left text-xs text-slate-700 transition hover:border-amber-400 hover:text-amber-800"
            >
              <span className="mr-1 text-slate-400">다음</span>
              {nextBook.volumeLabel || nextBook.title}
            </button>
          )}
        </section>
      )}

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-4xl">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">교재 기본 정보</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-[1fr_400px]">
            {/* 좌측 — 텍스트 입력 */}
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">제목 *</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => handleFormChange('title', e.target.value)}
                  placeholder="PRIME-A 4학년 1학기"
                  className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">부제</span>
                <input
                  type="text"
                  value={form.subtitle}
                  onChange={e => handleFormChange('subtitle', e.target.value)}
                  placeholder="선택사항"
                  className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">교재 라벨</span>
                  <input
                    type="text"
                    value={form.bookLabel}
                    onChange={e => handleFormChange('bookLabel', e.target.value)}
                    placeholder="PRIME-A"
                    className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">권 정보</span>
                  <input
                    type="text"
                    value={form.volumeLabel}
                    onChange={e => handleFormChange('volumeLabel', e.target.value)}
                    placeholder="1권"
                    className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">설명</span>
                <textarea
                  value={form.description}
                  onChange={e => handleFormChange('description', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>

              {/* 교재 메타데이터 */}
              <div className="pt-4 border-t border-slate-200 space-y-4">
                <h3 className="text-sm font-semibold text-slate-900">교재 메타데이터</h3>
                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">교재 유형</span>
                    <select
                      value={form.materialType}
                      onChange={e => handleFormChange(
                        'materialType',
                        e.target.value as PrintBookMaterialType | '',
                      )}
                      className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                    >
                      <option value="">선택 안 함</option>
                      <option value="CHECKUP_BOOK">체크업북</option>
                      <option value="WORKBOOK">워크북</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">제작 방식</span>
                    <select
                      value={form.sourceType}
                      onChange={e => handleFormChange('sourceType', e.target.value as PrintBookSourceType)}
                      className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                    >
                      <option value="GENERATED_PREVIEW">프리뷰 생성</option>
                      <option value="EXTERNAL_DESIGN">외부 제작본</option>
                    </select>
                  </label>
                </div>
                {isEdit && (
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">발주 상태</span>
                      <select
                        value={form.status}
                        onChange={e => handleFormChange('status', e.target.value as PrintBookStatus)}
                        className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                      >
                        {PRINT_BOOK_STATUSES.map(status => (
                          <option key={status} value={status}>{PRINT_BOOK_STATUS_LABELS[status]}</option>
                        ))}
                      </select>
                      <span className="mt-1 block text-xs text-slate-500">
                        교재 목록 배지·교재 진도 현황·진도 알림에 쓰입니다. 미발주인 권만 매일 오전 9시 발주 알림 대상입니다.
                      </span>
                    </label>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                      노출 범위
                      <PrintBookScopeHelp />
                    </span>
                    <select
                      value={form.scopeLevel}
                      onChange={e => setForm(prev => ({
                        ...prev,
                        scopeLevel: e.target.value as PrintBookScopeLevel | '',
                        scopeContentId: null,
                      }))}
                      className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                    >
                      <option value="">선택 안 함</option>
                      <option value="CURRICULUM">커리큘럼</option>
                      <option value="COURSE">코스</option>
                      <option value="GLOBAL">전체</option>
                    </select>
                  </label>
                  {(form.scopeLevel === 'CURRICULUM' || form.scopeLevel === 'COURSE') && (
                    <div>
                      <span className="text-sm font-medium text-slate-700">
                        {form.scopeLevel === 'CURRICULUM' ? '커리큘럼 콘텐츠 *' : '코스 콘텐츠 *'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsScopePickerOpen(true)}
                        className={`mt-1 flex min-h-10 w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm shadow-sm transition ${scopeContent ? 'border-amber-300 bg-amber-50/50' : 'border-slate-300 bg-white hover:border-amber-400'}`}
                      >
                        {scopeContentLoading ? (
                          <><Loader2 className="h-4 w-4 animate-spin text-slate-400" /> 불러오는 중...</>
                        ) : scopeContent ? (
                          <>
                            <ContentLevelBadge level={scopeContent.hierarchyLevel} />
                            <span className="min-w-0 flex-1 truncate font-medium text-slate-800"><ContentMetadataText text={scopeContent.title} /></span>
                            <span className="shrink-0 text-xs text-slate-400">#{scopeContent.id} · 변경</span>
                          </>
                        ) : (
                          <>
                            <Search className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-500">제목으로 검색해 선택</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.noticeEnabled}
                    onChange={e => handleFormChange('noticeEnabled', e.target.checked)}
                    className="rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-sm font-medium text-slate-700">학습자 알림 노출</span>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">알림 문구</span>
                  <textarea
                    value={form.noticeMessage}
                    onChange={e => handleFormChange('noticeMessage', e.target.value)}
                    rows={2}
                    placeholder="학습자에게 표시할 교재 안내 문구"
                    className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  />
                </label>
                {/* 다음 권 (교재 신청 알람용) — 과정 D/E처럼 유형이 전환되는 교재도 연결 가능 */}
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">다음 권</span>
                  <select
                    value={form.nextBookId ?? ''}
                    onChange={e => handleFormChange('nextBookId', e.target.value === '' ? null : Number(e.target.value))}
                    className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  >
                    <option value="">없음 (마지막 권)</option>
                    {(bookList ?? [])
                      .filter(b => b.id !== Number(id) && b.materialType != null)
                      .map(b => (
                        <option key={b.id} value={b.id}>
                          [{b.materialType === 'WORKBOOK' ? '워크북' : '체크업북'}] {b.title}
                          {b.volumeLabel ? ` (${b.volumeLabel})` : ''}
                        </option>
                      ))}
                  </select>
                  {nextBook && (
                    <button
                      type="button"
                      onClick={() => navigateToConnectedBook(nextBook.id)}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                    >
                      <Link2 className="h-3.5 w-3.5" /> 선택된 다음 권 상세로 이동
                    </button>
                  )}
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">샘플 PDF URL</span>
                  <input
                    type="text"
                    value={form.samplePdfUrl}
                    onChange={e => handleFormChange('samplePdfUrl', e.target.value)}
                    placeholder="https://..."
                    className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                  />
                </label>
                <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">해피데이 상품코드</span>
                    <input
                      type="number"
                      min={1}
                      max={999999999999}
                      value={form.happydayItemCode ?? ''}
                      onChange={e => handleFormChange(
                        'happydayItemCode',
                        e.target.value === '' ? null : Number(e.target.value),
                      )}
                      placeholder="예: 17092"
                      className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">ISBN</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={13}
                      value={form.happydayIsbnCode}
                      onChange={e => handleFormChange('happydayIsbnCode', e.target.value.replace(/\D/g, ''))}
                      placeholder="13자리 ISBN"
                      className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                    />
                  </label>
                  <p className="col-span-2 text-xs text-slate-500">
                    출고 연동에는 상품코드 또는 ISBN이 필요하며, 둘 다 입력하면 상품코드가 우선됩니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 우측 — 표지 이미지 업로드 */}
            <div className="flex flex-wrap content-start gap-6">
              <BookImageUploadField
                label="학습자 노출 이미지"
                value={form.learnerCoverImageUrl}
                onChange={(url) => handleFormChange('learnerCoverImageUrl', url)}
                previewAlt="학습자 노출 이미지 미리보기"
                helperText="오늘 수업과 PauseQuiz에 표시됩니다. 권수가 없는 공통 이미지를 등록할 수 있습니다."
              />
              <div>
              <span className="text-sm font-medium text-slate-700 block mb-2">표지 이미지</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => handleCoverFileSelected(e.target.files?.[0])}
              />
              {form.coverImageUrl ? (
                <div className="space-y-2">
                  <div
                    className="relative rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden"
                    style={{ width: 180, height: 253 }}
                  >
                    <img
                      src={form.coverImageUrl}
                      alt="표지 미리보기"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setUploadError(null); fileInputRef.current?.click() }}
                      disabled={isUploading}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors disabled:opacity-50"
                    >
                      <ImagePlus className="w-3.5 h-3.5" /> 교체
                    </button>
                    <button
                      type="button"
                      onClick={handleCoverDelete}
                      disabled={isUploading}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> 삭제
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setUploadError(null); fileInputRef.current?.click() }}
                  disabled={isUploading}
                  className="rounded-lg border-2 border-dashed border-slate-300 hover:border-amber-400 bg-slate-50 hover:bg-amber-50 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-amber-600 transition-colors disabled:opacity-50"
                  style={{ width: 180, height: 253 }}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span className="text-xs">업로드 중...</span>
                    </>
                  ) : (
                    <>
                      <ImagePlus className="w-6 h-6" />
                      <span className="text-xs">표지 이미지 업로드</span>
                    </>
                  )}
                </button>
              )}
              <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                권장: 216 × 303mm (도련 3mm 포함)<br />
                JPG / PNG / WebP, 최대 5MB
              </p>
              {uploadError && (
                <p className="mt-2 text-xs text-red-500 flex items-start gap-1">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{uploadError}</span>
                </p>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Module & Lesson Selection */}
      {step === 2 && (
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Content Tree */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-700">커리큘럼 트리</h2>
              <p className="text-xs text-slate-400">모듈의 + 버튼을 클릭하여 추가</p>
            </div>
            <div className="p-2 max-h-[600px] overflow-y-auto">
              <ContentTree onSelectModule={handleAddModule} />
            </div>
          </div>

          {/* Right: Selected Modules */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-700">
                선택된 구성 ({modules.length}개 모듈, {modules.reduce((sum, m) => sum + m.lessons.length, 0)}개 레슨)
              </h2>
            </div>
            <div className="p-3 max-h-[600px] overflow-y-auto space-y-3">
              {modules.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">
                  왼쪽 트리에서 모듈을 추가하세요
                </div>
              ) : (
                modules.map((mod) => (
                  <div key={mod.moduleId} className="border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-t-lg">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                        <FolderOpen className="w-4 h-4 text-teal-500" />
                        <span className="text-sm font-medium text-slate-700">
                          Module {mod.chapterNumber}: <ContentMetadataText text={getModuleDisplayName(mod)} />
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveModule(mod.moduleId)}
                        className="p-1 text-slate-400 hover:text-red-500 rounded"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {mod.lessons.map((lesson, li) => (
                        <div key={lesson.lessonId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <PlayCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <span className="text-slate-600 truncate">
                            L{String(li + 1).padStart(2, '0')}. <ContentMetadataText text={lesson.bareTitle || lesson.lessonName} />
                          </span>
                          {lesson.confirmed && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Page Summary */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-slate-700">페이지 구성 요약</h2>
              {isExternalDesign && (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                  외부 제작본 — 페이지 수동 입력
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">총 <strong className="text-slate-900">{displayTotalPages}</strong>페이지</span>
              <span className="text-slate-500">
                컨펌: <strong className="text-slate-900">
                  {modules.flatMap(m => m.lessons).filter(l => l.confirmed).length}
                  /{modules.flatMap(m => m.lessons).length}
                </strong>
              </span>
              {isEdit && isExternalDesign && (
                <div className="flex items-center gap-2">
                  {!serverExternalDesign ? (
                    <span className="text-xs text-slate-400">제작 방식을 먼저 저장하세요</span>
                  ) : pagesDirty || blockQuizPagesDirty ? (
                    <span className="text-xs text-slate-400">페이지 입력은 '페이지 저장' 버튼으로 별도 저장됩니다</span>
                  ) : null}
                  {manualPagesMutation.isSuccess && !manualPagesMutation.isPending && !pagesDirty && !blockQuizPagesDirty && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> 저장됨
                    </span>
                  )}
                  {manualPagesMutation.isError && (
                    <span className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> 저장 실패
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={openPageCsv}
                    disabled={!serverExternalDesign || modules.length === 0}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-amber-300 hover:bg-amber-50 disabled:opacity-50"
                  >
                    <FileUp className="h-3.5 w-3.5" /> CSV 벌크 입력
                  </button>
                  <button
                    onClick={handleSaveManualPages}
                    disabled={manualPagesMutation.isPending || !serverExternalDesign}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-xs font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
                  >
                    {manualPagesMutation.isPending
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Save className="w-3.5 h-3.5" />}
                    페이지 저장
                  </button>
                </div>
              )}
              {/* 외부 제작본은 전체 프리뷰 금지 — 프리뷰 autosave 가 수동 페이지를 덮어씀 */}
              {isEdit && !isExternalDesign && (
                <>
                  <span className="text-xs text-slate-400">
                    전체 프리뷰를 열면 블록퀴즈 시작 페이지가 자동 계산값으로 덮어써집니다
                  </span>
                  {blockQuizPagesMutation.isSuccess && !blockQuizPagesMutation.isPending && !blockQuizPagesDirty && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> 블록퀴즈 페이지 저장됨
                    </span>
                  )}
                  {blockQuizPagesMutation.isError && (
                    <span className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" /> 블록퀴즈 페이지 저장 실패
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => blockQuizPagesMutation.mutate()}
                    disabled={blockQuizPagesMutation.isPending || !blockQuizPagesDirty}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50"
                  >
                    {blockQuizPagesMutation.isPending
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Save className="w-3.5 h-3.5" />}
                    블록퀴즈 페이지 저장
                  </button>
                  <button
                    onClick={() => navigate(`/contents/books/${id}/preview-all`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-xs font-medium hover:bg-amber-400 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> 전체 프리뷰
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2 text-left font-semibold text-slate-500">구분</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">페이지 수</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">시작</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">끝</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">컨펌</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">액션</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {/* 속표지/목차/모듈요약/메모는 프리뷰 생성 레이아웃 산물 — 외부 제작본에서는 숨김 */}
                {!isExternalDesign && (
                <>
                {/* 속표지 Row */}
                <tr className="bg-purple-50/50">
                  <td className="px-4 py-2 font-medium text-slate-700 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-400" />
                    속표지
                  </td>
                  <td className="px-4 py-2 text-center text-slate-600">1</td>
                  <td className="px-4 py-2 text-center text-slate-600">1</td>
                  <td className="px-4 py-2 text-center text-slate-600">1</td>
                  <td className="px-4 py-2 text-center">-</td>
                  <td className="px-4 py-2 text-center">-</td>
                </tr>

                {/* 목차 Row */}
                <tr className="bg-blue-50/50">
                  <td className="px-4 py-2 font-medium text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-400" />
                    목차
                  </td>
                  <td className="px-4 py-2 text-center text-slate-600">{TOC_PAGE_COUNT}</td>
                  <td className="px-4 py-2 text-center text-slate-600">2</td>
                  <td className="px-4 py-2 text-center text-slate-600">{1 + TOC_PAGE_COUNT}</td>
                  <td className="px-4 py-2 text-center">-</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => {
                        const params = new URLSearchParams({
                          type: 'toc',
                          bookTitle: form.title,
                          bookLabel: form.bookLabel,
                          modules: JSON.stringify(calculatedModules.map(mod => ({
                            chapterNumber: mod.chapterNumber,
                            moduleName: getModuleDisplayName(mod),
                            lessons: mod.lessons.map(l => ({
                              lessonName: l.bareTitle || l.lessonName,
                              startPage: l.startPage,
                            })),
                          }))),
                        })
                        navigate(`/contents/books/${id || 'new'}/preview/toc?${params}`)
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Eye className="w-3 h-3" /> 프리뷰
                    </button>
                  </td>
                </tr>
                </>
                )}

                {displayModules.map((mod, mi) => (
                  <Fragment key={`module-${mod.moduleId}`}>
                    {/* Module header */}
                    <tr key={`mod-${mod.moduleId}`} className="bg-teal-50/50">
                      <td colSpan={6} className="px-4 py-2 font-medium text-teal-700 flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-teal-500" />
                        <label className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-semibold text-teal-700">
                          <span>Module</span>
                          <input
                            type="number"
                            min={1}
                            value={mod.chapterNumber}
                            aria-label={`${getModuleDisplayName(mod)} 모듈 번호`}
                            onChange={event => {
                              const nextNumber = event.target.valueAsNumber
                              setModules(currentModules => currentModules.map(module => (
                                module.moduleId === mod.moduleId
                                  ? { ...module, chapterNumber: Number.isFinite(nextNumber) ? Math.max(1, nextNumber) : 1 }
                                  : module
                              )))
                            }}
                            className="w-16 rounded-md border-slate-300 px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                          />
                          <span>:</span>
                          <input
                            type="text"
                            maxLength={200}
                            value={mod.titleOverride ?? (mod.bareTitle || mod.moduleName)}
                            aria-label={`Module ${mod.chapterNumber} 이름`}
                            onChange={event => {
                              const titleOverride = event.target.value
                              setModules(currentModules => currentModules.map(module => (
                                module.moduleId === mod.moduleId ? { ...module, titleOverride } : module
                              )))
                            }}
                            className="min-w-0 flex-1 rounded-md border-slate-300 px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                          />
                        </label>
                      </td>
                    </tr>

                    {/* Module summary pages */}
                    {!isExternalDesign && mod.moduleSummaryStart && (
                      <tr key={`mod-summary-${mod.moduleId}`} className="bg-indigo-50/30">
                        <td className="px-4 py-1.5 text-slate-500 pl-10 text-sm">모듈 요약</td>
                        <td className="px-4 py-1.5 text-center text-slate-500 text-sm">2</td>
                        <td className="px-4 py-1.5 text-center text-slate-500 text-sm">{mod.moduleSummaryStart}</td>
                        <td className="px-4 py-1.5 text-center text-slate-500 text-sm">{mod.moduleSummaryStart + 1}</td>
                        <td className="px-4 py-1.5 text-center">-</td>
                        <td className="px-4 py-1.5 text-center">
                          <button
                            onClick={() => {
                              const params = new URLSearchParams({
                                type: 'module-summary',
                                moduleIndex: String(mi),
                                chapterNumber: String(mod.chapterNumber),
                                moduleName: getModuleDisplayName(mod),
                                startPage: String(mod.moduleSummaryStart),
                                bookLabel: form.bookLabel,
                                lessons: JSON.stringify(mod.lessons.map(l => ({
                                  lessonName: l.bareTitle || l.lessonName,
                                  pageCount: l.pageCount,
                                }))),
                              })
                              navigate(`/contents/books/${id || 'new'}/preview/module-${mod.moduleId}?${params}`)
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Eye className="w-3 h-3" /> 프리뷰
                          </button>
                        </td>
                      </tr>
                    )}

                    {mod.lessons.map((lesson, li) => {
                      const endPage = lesson.startPage != null && lesson.pageCount != null
                        ? lesson.startPage + lesson.pageCount - 1
                        : null
                      return (
                        <Fragment key={`lesson-group-${lesson.lessonId}`}>
                          <tr key={`lesson-${lesson.lessonId}`} className="hover:bg-slate-50">
                            <td className="px-4 py-2 text-slate-600 pl-10">
                              <div>L{String(li + 1).padStart(2, '0')}. <ContentMetadataText text={lesson.bareTitle || lesson.lessonName} /></div>
                              <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                                <span>블록퀴즈 시작</span>
                                <input
                                  type="number"
                                  min={lesson.startPage ?? 1}
                                  max={endPage ?? undefined}
                                  value={lesson.blockQuizStartPage ?? ''}
                                  placeholder="-"
                                  aria-label={`${lesson.bareTitle || lesson.lessonName} 블록퀴즈 시작 페이지`}
                                  onChange={event => {
                                    const value = event.target.value === '' ? null : Number(event.target.value)
                                    markBlockQuizPagesDirty()
                                    setModules(currentModules => currentModules.map(module => ({
                                      ...module,
                                      lessons: module.lessons.map(currentLesson =>
                                        currentLesson.lessonId === lesson.lessonId
                                          ? { ...currentLesson, blockQuizStartPage: value }
                                          : currentLesson
                                      ),
                                    })))
                                  }}
                                  className="w-16 rounded border-slate-300 py-0.5 text-center text-xs focus:border-amber-500 focus:ring-amber-500"
                                />
                                <span>쪽</span>
                              </label>
                            </td>
                            <td className="px-4 py-2 text-center text-slate-600">
                              <input
                                type="number"
                                min={0}
                                value={lesson.pageCount ?? ''}
                                placeholder="-"
                                onChange={e => {
                                  const val = e.target.value === '' ? null : Number(e.target.value)
                                  markPagesDirty()
                                  setModules(prev => prev.map(m => ({
                                    ...m,
                                    lessons: m.lessons.map(l =>
                                      l.lessonId === lesson.lessonId ? { ...l, pageCount: val } : l
                                    )
                                  })))
                                }}
                                className="w-14 text-center rounded border-slate-300 text-sm py-0.5 focus:border-amber-500 focus:ring-amber-500"
                              />
                            </td>
                            <td className="px-4 py-2 text-center text-slate-600">
                              {isExternalDesign ? (
                                <input
                                  type="number"
                                  min={1}
                                  value={lesson.startPage ?? ''}
                                  placeholder="-"
                                  onChange={e => {
                                    const val = e.target.value === '' ? null : Number(e.target.value)
                                    markPagesDirty()
                                    setModules(prev => prev.map(m => ({
                                      ...m,
                                      lessons: m.lessons.map(l =>
                                        l.lessonId === lesson.lessonId ? { ...l, startPage: val } : l
                                      )
                                    })))
                                  }}
                                  className="w-14 text-center rounded border-slate-300 text-sm py-0.5 focus:border-amber-500 focus:ring-amber-500"
                                />
                              ) : (
                                lesson.startPage || '-'
                              )}
                            </td>
                            <td className="px-4 py-2 text-center text-slate-600">
                              {endPage != null && endPage > 0 ? endPage : '-'}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {isEdit ? (
                                <button
                                  onClick={() => confirmMutation.mutate({
                                    lessonId: lesson.lessonId,
                                    confirmed: !lesson.confirmed,
                                  })}
                                  disabled={confirmMutation.isPending}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                                    lesson.confirmed
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                  }`}
                                >
                                  {lesson.confirmed
                                    ? <><CheckCircle2 className="w-3 h-3" /> 완료</>
                                    : <><Clock className="w-3 h-3" /> 미확인</>
                                  }
                                </button>
                              ) : (
                                <span className="text-xs text-slate-300">저장 후 가능</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <div className="inline-flex gap-1">
                                <button
                                  onClick={() => {
                                    const parsed = lesson.grade ? parseGradeField(lesson.grade) : null
                                    const params = new URLSearchParams({
                                      name: lesson.bareTitle || lesson.lessonName,
                                      startPage: String(lesson.startPage || 6),
                                      bookLabel: form.bookLabel,
                                      chapter: String(li + 1).padStart(2, '0'),
                                      grade: parsed?.grade || '',
                                      unitLabel: parsed?.unitLabel || '',
                                      unitName: parsed?.unitName || '',
                                      quetureTitle: lesson.bareTitle || lesson.lessonName,
                                    })
                                    navigate(`/contents/books/${id || 'new'}/preview/${lesson.lessonId}?${params}`)
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                                >
                                  <Eye className="w-3 h-3" /> 프리뷰
                                </button>
                                <button
                                  onClick={() => {
                                    const params = new URLSearchParams({
                                      name: lesson.bareTitle || lesson.lessonName,
                                      startPage: String(lesson.startPage || 6),
                                      bookLabel: form.bookLabel,
                                      quetureTitle: lesson.bareTitle || lesson.lessonName,
                                    })
                                    navigate(`/contents/books/${id || 'new'}/answer-key/${lesson.lessonId}?${params}`)
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded"
                                >
                                  <FileText className="w-3 h-3" /> 정답&해설
                                </button>
                              </div>
                            </td>
                          </tr>
                          {!isExternalDesign && lesson.hasMemoPage && endPage != null && (
                            <tr key={`dummy-${lesson.lessonId}`} className="bg-slate-50/50">
                              <td className="px-4 py-1 text-xs text-slate-400 pl-10 italic">(메모 페이지)</td>
                              <td className="px-4 py-1 text-center text-xs text-slate-400">1</td>
                              <td className="px-4 py-1 text-center text-xs text-slate-400">{endPage + 1}</td>
                              <td className="px-4 py-1 text-center text-xs text-slate-400">{endPage + 1}</td>
                              <td className="px-4 py-1 text-center">-</td>
                              <td className="px-4 py-1 text-center">-</td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isScopePickerOpen && (form.scopeLevel === 'CURRICULUM' || form.scopeLevel === 'COURSE') && (
        <ContentPickerModal
          allowedLevels={[form.scopeLevel === 'CURRICULUM' ? 'curriculum' : 'course']}
          title={`${form.scopeLevel === 'CURRICULUM' ? '커리큘럼' : '코스'} 선택`}
          description="노출할 콘텐츠를 제목으로 검색해 선택하세요. 콘텐츠 ID는 자동으로 저장됩니다."
          onClose={() => setIsScopePickerOpen(false)}
          onSelect={(content) => {
            handleFormChange('scopeContentId', content.id)
            setIsScopePickerOpen(false)
          }}
        />
      )}

      {isPageCsvOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPageCsvOpen(false)
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="page-csv-title"
            className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h3 id="page-csv-title" className="text-lg font-bold text-slate-900">외부 제작본 페이지 벌크 입력</h3>
                <p className="mt-1 text-sm text-slate-500">CSV를 검증한 뒤 현재 화면에 적용합니다. 적용 후 상단의 ‘페이지 저장’을 눌러야 서버에 저장됩니다.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsPageCsvOpen(false)}
                aria-label="페이지 벌크 입력 닫기"
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-700">CSV 내용</p>
                  <p className="mt-0.5 text-xs text-slate-400">필수 헤더: lessonId, startPage, pageCount · 선택 헤더: blockQuizStartPage · 일부 레슨만 입력해도 됩니다.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPageCsvText(pageCsvTemplate)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                  >
                    현재 레슨 ID 채우기
                  </button>
                  <input
                    ref={pageCsvFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(event) => handlePageCsvFile(event.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => pageCsvFileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-amber-300 hover:bg-amber-50"
                  >
                    <FileUp className="h-4 w-4" /> CSV 파일 선택
                  </button>
                </div>
              </div>
              <textarea
                value={pageCsvText}
                onChange={(event) => setPageCsvText(event.target.value)}
                rows={10}
                spellCheck={false}
                className="block w-full rounded-xl border-slate-300 font-mono text-sm leading-6 shadow-sm focus:border-amber-500 focus:ring-amber-500"
              />

              {pageCsvResult.errors.length > 0 ? (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm font-semibold text-red-800">수정이 필요한 항목 {pageCsvResult.errors.length}개</p>
                  <ul className="mt-2 flex flex-col gap-1 text-sm text-red-700">
                    {pageCsvResult.errors.slice(0, 12).map((error, index) => (
                      <li key={`${error.rowNumber}-${error.code}-${index}`}>{error.rowNumber}행 · {error.message}</li>
                    ))}
                  </ul>
                  {pageCsvResult.errors.length > 12 && (
                    <p className="mt-2 text-xs font-medium text-red-600">외 {pageCsvResult.errors.length - 12}개 오류</p>
                  )}
                </div>
              ) : pageCsvResult.rows.length > 0 ? (
                <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between bg-emerald-50 px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800">
                      <CheckCircle2 className="h-4 w-4" /> {pageCsvResult.rows.length}개 레슨 검증 완료
                    </span>
                    <span className="text-xs text-emerald-700">적용 전 미리보기</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="border-y border-slate-200 bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left">lessonId</th>
                        <th className="px-4 py-2 text-center">시작</th>
                        <th className="px-4 py-2 text-center">페이지 수</th>
                        <th className="px-4 py-2 text-center">끝</th>
                        <th className="px-4 py-2 text-center">블록퀴즈 시작</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pageCsvResult.rows.slice(0, 8).map(row => (
                        <tr key={row.lessonId}>
                          <td className="px-4 py-2 font-medium text-slate-700">{row.lessonId}</td>
                          <td className="px-4 py-2 text-center text-slate-600">{row.startPage}</td>
                          <td className="px-4 py-2 text-center text-slate-600">{row.pageCount}</td>
                          <td className="px-4 py-2 text-center text-slate-600">{row.endPage}</td>
                          <td className="px-4 py-2 text-center text-slate-600">{row.blockQuizStartPage ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {pageCsvResult.rows.length > 8 && (
                    <p className="border-t border-slate-100 px-4 py-2 text-center text-xs text-slate-400">외 {pageCsvResult.rows.length - 8}개 레슨</p>
                  )}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                  <FileUp className="mx-auto h-6 w-6 text-slate-300" />
                  <p className="mt-2 text-sm font-medium text-slate-600">CSV 파일을 선택하거나 내용을 붙여넣으세요</p>
                  <p className="mt-1 text-xs text-slate-400">현재 교재의 lessonId가 필요하면 ‘현재 레슨 ID 채우기’를 사용할 수 있습니다.</p>
                </div>
              )}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setIsPageCsvOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200"
              >
                취소
              </button>
              <button
                type="button"
                onClick={applyPageCsv}
                disabled={pageCsvResult.hasErrors || pageCsvResult.rows.length === 0}
                className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                페이지 값 적용
              </button>
            </footer>
          </section>
        </div>
      )}

    </div>
  )
}
